import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { calendarAPI } from '../api/client';
import Layout from '../components/Layout';
import '../styles/calendar-settings.css';

function timeAgo(iso) {
  if (!iso) return 'never';
  const mins = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function providerIcon(provider) {
  if (provider === 'google') return 'G';
  if (provider === 'ics_url') return '~';
  return 'F';
}

export default function ConnectCalendar() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [syncingId, setSyncingId] = useState(null);

  const [icsUrl, setIcsUrl] = useState('');
  const [icsLabel, setIcsLabel] = useState('');
  const [addingIcs, setAddingIcs] = useState(false);

  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLabel, setUploadLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadConnections = async () => {
    try {
      const r = await calendarAPI.getConnections();
      setConnections(r.data || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    loadConnections();
    const googleParam = searchParams.get('google');
    const errorParam = searchParams.get('error');
    if (googleParam === 'connected') {
      showToast('Google Calendar connected!', 'success');
      setSearchParams({});
    } else if (errorParam) {
      const msgs = { state_invalid: 'Auth failed — please try again.', access_denied: 'Access denied.', oauth_failed: 'Connection failed — please try again.' };
      showToast(msgs[errorParam] || 'Something went wrong.', 'error');
      setSearchParams({});
    }
  }, []);

  const handleDisconnect = async (id) => {
    if (!window.confirm('Disconnect this calendar? All imported events will be removed.')) return;
    await calendarAPI.deleteConnection(id).catch(() => {});
    setConnections(prev => prev.filter(c => c.id !== id));
    showToast('Calendar disconnected.');
  };

  const handleSync = async (id) => {
    setSyncingId(id);
    try {
      const r = await calendarAPI.syncConnection(id);
      showToast(`Synced ${r.data.synced} events.`);
      await loadConnections();
    } catch (err) {
      showToast(err.response?.data?.error || 'Sync failed.', 'error');
    }
    setSyncingId(null);
  };

  const handleAddIcsUrl = async () => {
    if (!icsUrl.trim()) return;
    setAddingIcs(true);
    try {
      await calendarAPI.addIcsUrl(icsUrl.trim(), icsLabel.trim() || undefined);
      setIcsUrl('');
      setIcsLabel('');
      showToast('Calendar subscribed!');
      await loadConnections();
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not subscribe to that URL.', 'error');
    }
    setAddingIcs(false);
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    if (uploadLabel.trim()) formData.append('label', uploadLabel.trim());
    try {
      await calendarAPI.uploadIcs(formData);
      setUploadFile(null);
      setUploadLabel('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      showToast('Calendar imported!');
      await loadConnections();
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not import file.', 'error');
    }
    setUploading(false);
  };

  return (
    <Layout title="Calendars">
    <div className="cal-settings-page">
      <div className="cal-settings-body">
        <div className="cal-settings-inner">

          {/* Connected calendars */}
          <div className="cal-section-card">
            <div className="cal-section-header">
              <h2 className="cal-section-title">Connected</h2>
            </div>
            {loading ? (
              <p className="cal-empty-connections">Loading…</p>
            ) : connections.length === 0 ? (
              <p className="cal-empty-connections">No calendars connected yet.</p>
            ) : (
              <div className="cal-connection-list">
                {connections.map(conn => (
                  <div key={conn.id} className="cal-connection-item">
                    <div className={`cal-connection-icon ${conn.provider}`}>
                      {providerIcon(conn.provider)}
                    </div>
                    <div className="cal-connection-info">
                      <p className="cal-connection-label">{conn.label}</p>
                      <p className="cal-connection-synced">Synced {timeAgo(conn.last_synced_at)}</p>
                    </div>
                    <div className="cal-connection-actions">
                      {conn.provider !== 'ics_file' && (
                        <button
                          className="cal-btn-sync"
                          onClick={() => handleSync(conn.id)}
                          disabled={syncingId === conn.id}
                        >
                          {syncingId === conn.id ? '…' : 'Sync'}
                        </button>
                      )}
                      <button
                        className="cal-btn-disconnect"
                        onClick={() => handleDisconnect(conn.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Google Calendar */}
          <div className="cal-section-card">
            <h2 className="cal-section-title" style={{ marginBottom: 8 }}>Google Calendar</h2>
            <p className="cal-section-desc">
              Connect your Google account to import events directly. Events stay private — only you can see them.
            </p>
            <button
              className="cal-btn-google"
              onClick={async () => {
                try {
                  const r = await calendarAPI.getGoogleAuthUrl('/calendar-settings');
                  window.location.href = r.data.url;
                } catch { alert('Could not start Google auth. Check your server config.'); }
              }}
            >
              Connect Google Calendar
            </button>
          </div>

          {/* ICS URL */}
          <div className="cal-section-card">
            <h2 className="cal-section-title" style={{ marginBottom: 8 }}>Subscribe via ICS URL</h2>
            <p className="cal-section-desc">
              Paste an ICS link from Apple Calendar, Outlook, or any calendar that supports ICS export.
            </p>
            <div className="cal-form">
              <input
                className="cal-input"
                placeholder="https://calendar.google.com/calendar/ical/…"
                value={icsUrl}
                onChange={e => setIcsUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddIcsUrl()}
              />
              <input
                className="cal-input"
                placeholder="Label (optional)"
                value={icsLabel}
                onChange={e => setIcsLabel(e.target.value)}
              />
              <button
                className="cal-btn-submit"
                onClick={handleAddIcsUrl}
                disabled={addingIcs || !icsUrl.trim()}
              >
                {addingIcs ? 'Subscribing…' : 'Subscribe'}
              </button>
            </div>
          </div>

          {/* ICS file upload */}
          <div className="cal-section-card">
            <h2 className="cal-section-title" style={{ marginBottom: 8 }}>Import .ics File</h2>
            <p className="cal-section-desc">
              Export a .ics file from your calendar app and import it here.
            </p>
            <div className="cal-form">
              <div className="cal-file-row">
                <label
                  className={`cal-file-label ${uploadFile ? 'has-file' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadFile ? uploadFile.name : 'Choose .ics file…'}
                </label>
                <input
                  ref={fileInputRef}
                  className="cal-file-input"
                  type="file"
                  accept=".ics"
                  onChange={e => setUploadFile(e.target.files[0] || null)}
                />
              </div>
              <input
                className="cal-input"
                placeholder="Label (optional)"
                value={uploadLabel}
                onChange={e => setUploadLabel(e.target.value)}
              />
              <button
                className="cal-btn-submit"
                onClick={handleUpload}
                disabled={uploading || !uploadFile}
              >
                {uploading ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>

          {/* Coming soon */}
          <div className="cal-section-card">
            <h2 className="cal-section-title" style={{ marginBottom: 12 }}>Coming soon</h2>
            <div className="cal-coming-soon-list">
              <div className="cal-coming-soon-item">
                <div className="cal-coming-soon-icon">P</div>
                <div className="cal-coming-soon-info">
                  <div className="cal-coming-soon-name">Partiful</div>
                </div>
                <span className="cal-badge-soon">Soon</span>
              </div>
              <div className="cal-coming-soon-item">
                <div className="cal-coming-soon-icon">f</div>
                <div className="cal-coming-soon-info">
                  <div className="cal-coming-soon-name">Facebook Events</div>
                </div>
                <span className="cal-badge-soon">Soon</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {toast && (
        <div className={`cal-toast ${toast.type}`}>{toast.msg}</div>
      )}
    </div>
    </Layout>
  );
}
